import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ConfirmDialog, Field, KeyText, StatusChip } from "./ui";
import CustomerPicker from "./CustomerPicker";
import { db } from "../firebase";
import { useSession } from "../auth/AuthProvider";
import { productName } from "../config";
import { formatDate, isDevelopment, licenseStatus } from "../lib/licenses";
import {
  createCustomer,
  extendLicense,
  removeLicense,
  setDevelopment,
  updateLicenseDetails,
} from "../lib/operations";

const EDITABLE = ["customer", "customerEmail", "location", "remarks"];

const pick = (license) => ({
  ...Object.fromEntries(EDITABLE.map((f) => [f, license?.[f] || ""])),
  customerId: license?.customerId || "",
});

function defaultExtension(expirydate) {
  const base = expirydate ? new Date(expirydate) : new Date();
  if (Number.isNaN(base.getTime())) return "";
  base.setFullYear(base.getFullYear() + 1);
  return base.toISOString().slice(0, 10);
}

// Details + edit dialog for one licence. Admins (canManage) can also extend
// the expiry date and delete/revoke keys that haven't been activated.
// The key can be linked to a customer record of its own distributor (LiFE
// direct customers for keys without a distributor).
export default function LicenseDialog({
  license,
  onClose,
  canManage = false,
  actor,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const session = useSession();
  const [form, setForm] = useState(pick(license));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [extendTo, setExtendTo] = useState(null);
  const [confirm, setConfirm] = useState(null); // "extend" | "remove"
  const [working, setWorking] = useState(false);

  // The linked record, for the picker's label.
  const [linked, setLinked] = useState(null);
  const id = license?.id;
  const linkedId = license?.customerId || null;
  useEffect(() => {
    let cancelled = false;
    setLinked(null);
    if (linkedId) {
      getDoc(doc(db, "customers", linkedId))
        .then(
          (snap) =>
            !cancelled &&
            setLinked(
              snap.exists()
                ? { id: snap.id, ...snap.data() }
                : { id: linkedId, name: "Deleted customer record" },
            ),
        )
        .catch(
          () =>
            !cancelled && setLinked({ id: linkedId, name: "Customer record" }),
        );
    }
    return () => {
      cancelled = true;
    };
  }, [id, linkedId]);

  useEffect(() => {
    setForm(pick(license));
    setError(null);
    setExtendTo(null);
    // Reset only when a different licence is opened, not on live updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!license) return null;

  const status = licenseStatus(license);
  const customerIdChanged = (license.customerId || "") !== form.customerId;
  const dirty =
    customerIdChanged || EDITABLE.some((f) => (license[f] || "") !== form[f]);
  // Distributors can only create records for their own keys; admins for any.
  const canSaveRecord =
    !form.customerId &&
    form.customer.trim() &&
    (canManage || license.distributorId);
  const isQuotaKey = !!(license.distributorId && license.issuedAt);
  const canRemove = canManage && license.status === "free";
  const canExtend = canManage && license.status === "used";

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const { customerId, ...details } = form;
      await updateLicenseDetails(
        license.product,
        license.id,
        customerIdChanged ? { ...details, customerId } : details,
      );
    } catch (e) {
      console.error(e);
      setError(
        "Couldn't save changes. You may not have permission to edit this key.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function toggleDevelopment(event) {
    setError(null);
    try {
      await setDevelopment({
        productId: license.product,
        licenseIds: [license.id],
        development: event.target.checked,
        actor,
      });
    } catch (e) {
      console.error(e);
      setError("Couldn't change the development flag.");
    }
  }

  // Turn the customer typed on this key into a customer record and link it.
  async function saveAsRecord() {
    setSaving(true);
    setError(null);
    try {
      const customerId = await createCustomer({
        distributorId: license.distributorId || null,
        fields: {
          name: form.customer,
          email: form.customerEmail,
          location: form.location,
        },
        actor: actor || session.email,
      });
      const { customerId: _unused, ...details } = form;
      await updateLicenseDetails(license.product, license.id, {
        ...details,
        customerId,
      });
      setForm((f) => ({ ...f, customerId }));
      setLinked({ id: customerId, name: form.customer });
    } catch (e) {
      console.error(e);
      setError("Couldn't create the customer record.");
    } finally {
      setSaving(false);
    }
  }

  async function runConfirmed() {
    setWorking(true);
    setError(null);
    try {
      if (confirm === "extend") {
        await extendLicense({
          productId: license.product,
          licenseId: license.id,
          expirydate: extendTo,
          actor,
        });
        setExtendTo(null);
        setConfirm(null);
      } else {
        await removeLicense({
          productId: license.product,
          licenseId: license.id,
          actor,
        });
        setConfirm(null);
        onClose();
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "That didn't work. Please try again.");
      setConfirm(null);
    } finally {
      setWorking(false);
    }
  }

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  function linkCustomer(record) {
    setLinked(record);
    setForm((f) => ({
      ...f,
      customerId: record?.id || "",
      // Fill blanks from the record; keep anything already typed.
      customer: f.customer || record?.name || "",
      customerEmail: f.customerEmail || record?.email || "",
      location: f.location || record?.location || "",
    }));
  }

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        fullScreen={fullScreen}
      >
        <DialogTitle>Licence key</DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              mb: 0.5,
            }}
          >
            <KeyText value={license.licenseKey || license.id} large />
            <StatusChip status={status} />
          </Box>
          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            {productName(license.product)} · {license.type || "—"}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            <Field label="Distributor">{license.distributor}</Field>
            <Field label="Region">{license.region}</Field>
            <Field label="Expiry">{license.expirydate}</Field>
            <Field label="Issued">
              {license.issuedAt
                ? `${formatDate(license.issuedAt)} · ${license.issuedBy || ""}`
                : null}
            </Field>
            <Field label="Activated">
              {license.registeredDate
                ? formatDate(license.registeredDate)
                : null}
            </Field>
            <Field label="Created">
              {`${formatDate(license.createdOn)}${license.createdBy ? ` · ${license.createdBy}` : ""}`}
            </Field>
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Customer details
          </Typography>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              pt: 1,
              gridTemplateColumns: { sm: "1fr 1fr" },
            }}
          >
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <CustomerPicker
                owner={license.distributorId || null}
                value={
                  form.customerId
                    ? linked || { id: form.customerId, name: "…" }
                    : null
                }
                onChange={linkCustomer}
                helperText="Optional: link this key to a saved customer"
              />
            </Box>
            <TextField
              label="Customer"
              value={form.customer}
              onChange={set("customer")}
              helperText={
                canSaveRecord ? (
                  <Link
                    component="button"
                    type="button"
                    onClick={saveAsRecord}
                    disabled={saving}
                  >
                    Save as customer record
                  </Link>
                ) : undefined
              }
            />
            <TextField
              label="Customer email"
              type="email"
              value={form.customerEmail}
              onChange={set("customerEmail")}
            />
            <TextField
              label="Project / location"
              value={form.location}
              onChange={set("location")}
              sx={{ gridColumn: { sm: "1 / -1" } }}
            />
            <TextField
              label="Remarks"
              value={form.remarks}
              onChange={set("remarks")}
              multiline
              minRows={2}
              sx={{ gridColumn: { sm: "1 / -1" } }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {canManage && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" gutterBottom>
                Manage key
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDevelopment(license)}
                    onChange={toggleDevelopment}
                  />
                }
                label="Development key — keeps working, but left out of counts and trends"
                sx={{ mb: 1.5, display: "flex" }}
              />
              {canExtend && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  {extendTo === null ? (
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() =>
                        setExtendTo(defaultExtension(license.expirydate))
                      }
                    >
                      Extend expiry…
                    </Button>
                  ) : (
                    <>
                      <TextField
                        type="date"
                        label="New expiry date"
                        value={extendTo}
                        onChange={(e) => setExtendTo(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      <Button
                        variant="contained"
                        disabled={!extendTo}
                        onClick={() => setConfirm("extend")}
                      >
                        Extend
                      </Button>
                      <Button color="inherit" onClick={() => setExtendTo(null)}>
                        Cancel
                      </Button>
                    </>
                  )}
                </Box>
              )}
              {canRemove && (
                <Box>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setConfirm("remove")}
                  >
                    {isQuotaKey ? "Revoke key" : "Delete key"}
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="p"
                    sx={{ mt: 0.5 }}
                  >
                    {isQuotaKey
                      ? "Deletes the key and returns it to the distributor's quota. Only possible before activation."
                      : "Permanently deletes this unused key."}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button color="inherit" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!dirty || saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirm === "extend"}
        title="Extend licence?"
        body={`${license.licenseKey}: expiry changes from ${license.expirydate || "—"} to ${extendTo}.`}
        confirmLabel="Extend"
        busy={working}
        onConfirm={runConfirmed}
        onClose={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === "remove"}
        title={isQuotaKey ? "Revoke this key?" : "Delete this key?"}
        body={
          isQuotaKey
            ? `${license.licenseKey} will stop working and ${license.distributor || "the distributor"} gets one ${license.type} key back in their quota.`
            : `${license.licenseKey} will be permanently deleted. This can't be undone.`
        }
        confirmLabel={isQuotaKey ? "Revoke" : "Delete"}
        destructive
        busy={working}
        onConfirm={runConfirmed}
        onClose={() => setConfirm(null)}
      />
    </>
  );
}
